import { useMemo, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatQty } from '@servwell/shared';
import { useTRPC } from '../../lib/trpc';
import { createIntentKey } from '../../lib/idempotency';
import { useSession } from '../session';
import { useOnline, useUnsentWrite, useQueueRead } from '../ui/useConnection';
import { useFailedWriteFor } from '../../lib/failed-writes';
import { Notice } from '../ui/Badge';
import { Button } from '../ui/Button';
import { EmptyState } from '../ui/EmptyState';
import {
  mutationState,
  ErrorState,
  OfflineState,
  shownState,
  refreshFailed,
  StaleNotice,
} from '../ui/QueryStates';
import { Sheet } from '../ui/Sheet';
import { Skeleton } from '../ui/Skeleton';
import { Field, field } from '../create/Field';
import type { OnHandItem, StockLocation } from '../inventory/types';
import { TransferAllocationPicker } from '../inventory/TransferAllocationPicker';
import { IconInventory, IconChevronLeft, IconPlus } from '../icons';
import { RestockCards } from '../inventory/RestockSheet';

const bigButton =
  'flex min-h-[56px] w-full items-center justify-center gap-2 rounded-[var(--radius)] text-base font-bold';

/**
 * "What's on my truck" (PRD v2 §5, §6.2). The tech's half of inventory is two questions —
 * do I have it, and can I go get more — so this screen is those two and nothing else.
 *
 * Restocking is a one-step transfer, not a request-and-approve flow: the tech who walks
 * into the warehouse and takes four capacitors has already done the thing, and a system
 * that makes them ask permission afterwards just doesn't get told.
 */
export function TechVanPage() {
  const trpc = useTRPC();
  const own = useQuery(trpc.restocks.ownVan.queryOptions());
  const session = useSession(),
    online = useOnline();
  const [restocking, setRestocking] = useState(false);
  const [search, setSearch] = useState('');
  const [notice, setNotice] = useState<string | null>(null);
  const van = own.data?.van;
  const q = search.trim().toLowerCase();
  const mine = (own.data?.items ?? []).filter(
    (i) =>
      !q || i.name.toLowerCase().includes(q) || (i.sku ?? '').toLowerCase().includes(q),
  );
  const header = (
    <div className="space-y-1">
      <Link
        to="/tech"
        className="inline-flex min-h-11 items-center gap-1 text-sm text-muted-foreground hover:text-text"
      >
        <IconChevronLeft size={15} />
        My day
      </Link>
      <h1 className="break-words font-heading text-xl font-bold leading-tight">
        {van?.name ?? 'My van'}
      </h1>
    </div>
  );
  const state = shownState(own, own.data);
  if (state === 'offline')
    return (
      <div className="space-y-4 p-4">
        {header}
        <OfflineState icon={IconInventory} what="what's on your van" />
      </div>
    );
  if (state === 'loading')
    return (
      <div className="space-y-3 p-4">
        <Skeleton className="h-16" />
        <Skeleton className="h-40" />
      </div>
    );
  if (state === 'error')
    return (
      <div className="space-y-4 p-4">
        {header}
        <ErrorState
          icon={IconInventory}
          what="what's on your van"
          onRetry={() => void own.refetch()}
        />
      </div>
    );
  if (!van)
    return (
      <div className="space-y-4 p-4">
        {header}
        <EmptyState
          icon={IconInventory}
          title="No van set up for you yet"
          description="The office assigns a van in Inventory. Once they do, what's on it shows up here."
        />
      </div>
    );
  return (
    <div className="space-y-4 p-4 pb-24">
      {header}
      <StaleNotice
        show={refreshFailed(own, own.data)}
        onRetry={() => void own.refetch()}
      />
      {!online ? (
        <Notice tone="warn">Offline. Showing the last saved van quantities.</Notice>
      ) : null}
      {notice ? <Notice tone="warn">{notice}</Notice> : null}
      <RestockCards
        key={`${session.orgId}:${session.userId}:${van.id}`}
        vanId={van.id}
        itemNames={new Map(mine.map((item) => [item.itemId, item.name]))}
      />
      <Button className={bigButton} onClick={() => setRestocking(true)}>
        <IconPlus size={17} />
        Restock my truck
      </Button>
      <input
        className={`${field} min-h-11`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search what's on board"
        aria-label="Search what's on your van"
      />
      {mine.length === 0 ? (
        <EmptyState
          icon={IconInventory}
          title={search ? 'Nothing matches that' : 'Your van is empty on the books'}
          description={
            search
              ? 'Try a different part name.'
              : 'Pull parts from the warehouse and they show up here.'
          }
        />
      ) : (
        <ul className="divide-y divide-border rounded-[var(--radius)] border border-border bg-surface">
          {mine.map((i) => (
            <li key={i.itemId} className="flex items-start gap-3 px-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="break-words text-sm font-medium">{i.name}</p>
                {i.sku ? <p className="text-xs text-muted-foreground">{i.sku}</p> : null}
                {i.source ? (
                  <p className="text-xs text-muted-foreground">
                    Keep {formatQty(i.keepAtLeastHundredths!)} · Fill{' '}
                    {formatQty(i.fillToHundredths!)}
                  </p>
                ) : null}
                <p className="text-xs text-muted-foreground">
                  Incoming {formatQty(i.onOrderHundredths)}
                </p>
                {!i.active ? (
                  <p className="text-xs text-muted-foreground">Retired part</p>
                ) : null}
              </div>
              <span
                className={`shrink-0 text-base font-bold tabular-nums ${i.onHandHundredths < 0 ? 'text-[var(--sw-danger-text)]' : ''}`}
              >
                {formatQty(i.onHandHundredths)}
              </span>
            </li>
          ))}
        </ul>
      )}
      {mine.some((i) => i.onHandHundredths < 0) ? (
        <p className="text-xs text-muted-foreground">
          A number below zero means parts went out that the books didn't have. The office
          will square it up.
        </p>
      ) : null}
      {restocking ? (
        <RestockSheet
          key={`${session.orgId}:${session.userId}:${van.id}`}
          vanId={van.id}
          onClose={() => setRestocking(false)}
          onWarn={setNotice}
        />
      ) : null}
    </div>
  );
}

/** Pull parts onto the truck: pick a part, pick how many, done. */
export function RestockSheet({
  vanId,
  onClose,
  onWarn,
}: {
  vanId: string;
  onClose: () => void;
  onWarn: (message: string | null) => void;
}) {
  const trpc = useTRPC();
  const qc = useQueryClient();
  const session = useSession();
  const [keys] = useState(() => createIntentKey());
  const stock = useQuery(trpc.inventory.onHand.queryOptions());
  const items = (stock.data?.items ?? []) as OnHandItem[];
  const locations = (stock.data?.locations ?? []) as StockLocation[];
  const [search, setSearch] = useState('');
  const [itemId, setItemId] = useState<string | null>(null);
  const [qty, setQty] = useState('1');
  // Everywhere that isn't the van is somewhere parts could come from; the warehouse is
  // nearly always the answer, so it leads.
  const sources = locations.filter((l) => l.id !== vanId);
  const [chosenFrom, setFromId] = useState<string | null>(null);
  const fromId = chosenFrom ?? sources[0]?.id ?? '';
  const [allocation, setAllocation] = useState<{
    itemId: string;
    fromId: string;
    lineId: string;
  } | null>(null);
  const workOrderLineId =
    allocation?.itemId === itemId && allocation.fromId === fromId
      ? allocation.lineId
      : '';
  const unsent = useUnsentWrite('inventory.transfer', itemId ?? undefined, 'itemId');
  const failed = useFailedWriteFor('inventory.transfer', itemId ?? undefined, 'itemId');
  const blocked = unsent || failed;
  const queueRead = useQueueRead();

  const transfer = useMutation(
    trpc.inventory.transfer.mutationOptions({
      onSuccess: (r) => {
        keys.clear();
        onWarn(
          r.wentNegative
            ? `Loaded. That leaves ${formatQty(r.fromNowHundredths)} where it came from — worth a count.`
            : null,
        );
        void qc.invalidateQueries();
        onClose();
      },
    }),
  );

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter(
        (i) =>
          !q ||
          i.name.toLowerCase().includes(q) ||
          (i.sku ?? '').toLowerCase().includes(q),
      )
      .slice(0, 40);
  }, [items, search]);

  const transferPhase = mutationState(transfer);
  const selected = items.find((i) => i.id === itemId) ?? null;
  const atSource = selected
    ? (selected.cells.find((c) => c.locationId === fromId)?.totalHundredths ?? 0)
    : 0;
  const canSave =
    Boolean(selected) &&
    Number.isFinite(Number(qty)) &&
    Number(qty) > 0 &&
    fromId &&
    !transfer.isPending &&
    !blocked &&
    stock.data !== undefined;

  return (
    <Sheet
      open
      onClose={() => {
        if (transferPhase !== 'sending') onClose();
      }}
      title="Restock my truck"
      footer={
        <Button
          className={`${bigButton} w-full`}
          disabled={!canSave}
          onClick={() => {
            if (!canSave) return;
            const payload = {
              itemId: selected!.id,
              fromLocationId: fromId,
              toLocationId: vanId,
              qty: Number(qty),
              workOrderLineId: workOrderLineId || undefined,
            };
            transfer.mutate({
              ...payload,
              ...keys.forIntent([
                session.orgId,
                session.userId,
                'inventory.transfer',
                payload,
              ]),
            });
          }}
        >
          {transferPhase === 'queued'
            ? 'Queued — see Unsent work'
            : transferPhase === 'sending'
              ? 'Loading…'
              : 'Load it on my truck'}
        </Button>
      }
    >
      <div className="space-y-3">
        {blocked ? (
          <Notice tone="warn">
            {queueRead
              ? 'This transfer is in Unsent work. Review or retry its original entry there before making another transfer for this part.'
              : 'Checking this phone for unsent transfers. Please wait.'}
          </Notice>
        ) : null}
        <StaleNotice
          show={refreshFailed(stock, stock.data)}
          onRetry={() => void stock.refetch()}
        />
        {shownState(stock, stock.data) === 'offline' ? (
          <OfflineState icon={IconInventory} what="stock locations and parts" />
        ) : shownState(stock, stock.data) === 'error' ? (
          <ErrorState
            icon={IconInventory}
            what="stock locations and parts"
            onRetry={() => void stock.refetch()}
          />
        ) : shownState(stock, stock.data) === 'loading' ? (
          <Skeleton className="h-40" />
        ) : null}
        {selected ? (
          <div className="rounded-[var(--radius)] border border-border bg-surface-2 p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="truncate font-medium">{selected.name}</p>
                <p className="text-xs text-muted-foreground">
                  {formatQty(atSource)} available there
                </p>
              </div>
              <Button
                variant="ghost"
                disabled={transfer.isPending}
                onClick={() => setItemId(null)}
              >
                Change
              </Button>
            </div>
          </div>
        ) : null}
        <fieldset
          className="min-w-0 space-y-3"
          disabled={transfer.isPending || blocked || stock.data === undefined}
        >
          {transfer.error ? (
            <Notice tone="danger">{transfer.error.message}</Notice>
          ) : null}

          {stock.data === undefined ? null : selected ? (
            <>
              <Field label="Take from">
                <select
                  className={`${field} min-h-11`}
                  value={fromId}
                  onChange={(e) => setFromId(e.target.value)}
                >
                  {sources.map((l) => (
                    <option key={l.id} value={l.id}>
                      {' '.repeat(l.depth * 2)}
                      {l.name}
                    </option>
                  ))}
                </select>
              </Field>

              {fromId ? (
                <TransferAllocationPicker
                  itemId={selected.id}
                  fromLocationId={fromId}
                  value={workOrderLineId}
                  onChange={(lineId) =>
                    setAllocation({ itemId: selected.id, fromId, lineId })
                  }
                />
              ) : null}
              <Field label="How many">
                <input
                  className={`${field} min-h-11`}
                  value={qty}
                  onChange={(e) => setQty(e.target.value)}
                  inputMode="decimal"
                  autoFocus
                />
              </Field>
            </>
          ) : (
            <>
              <input
                className={`${field} min-h-11`}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search parts"
                aria-label="Search parts"
                autoFocus
              />
              {matches.length === 0 ? (
                <p className="py-6 text-center text-sm text-muted-foreground">
                  {items.length === 0
                    ? 'The shop isn’t counting any parts yet.'
                    : 'Nothing matches that.'}
                </p>
              ) : (
                <ul className="max-h-[50vh] divide-y divide-border overflow-y-auto text-sm">
                  {matches.map((i) => {
                    const there =
                      i.cells.find((c) => c.locationId === fromId)?.totalHundredths ?? 0;
                    return (
                      <li key={i.id}>
                        <button
                          type="button"
                          className="flex min-h-[48px] w-full items-center gap-3 py-2 text-left hover:bg-surface-2"
                          onClick={() => setItemId(i.id)}
                        >
                          <span className="min-w-0 flex-1">
                            <span className="block truncate font-medium">{i.name}</span>
                            {i.sku ? (
                              <span className="block text-xs text-muted-foreground">
                                {i.sku}
                              </span>
                            ) : null}
                          </span>
                          <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
                            {formatQty(there)} there
                          </span>
                        </button>
                      </li>
                    );
                  })}
                </ul>
              )}
            </>
          )}
        </fieldset>
      </div>
    </Sheet>
  );
}
